import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { throttle } from '@thd-olt-functional/utils';
import { useLiveAgentSession } from '../platform/session/session';
import { useScrollLock } from './useMAScrollLock';
import {
  DRAWER_HEIGHTS,
  DRAG_THRESHOLD_PX,
  MINIMIZED_DRAG_MAX,
  OPEN_DRAG_MAX,
  TAP_DURATION_MS
} from '../shared/constants';

/**
 * Custom hook to handle the state and logic of a draggable drawer
 *
 * @param { DrawerCommon } props - The properties for the useDraggableDrawer hook.
 * @returns {{
 *   handlePointerDown: (event: TouchEvent) => void,
 *   handlePointerMove: (event: TouchEvent) => void,
 *   handlePointerUp: () => void,
 *   dynamicHeight: string,
 *   dynamicBottom: string | undefined,
 * }}
 */
export const useDraggableDrawer = (drawerState, setDrawerState, currentHeight) => {
  /**
   * @type {[DrawerStatus, React.Dispatch<React.SetStateAction<DrawerStatus>>]}
   */
  const startY = useRef(null);
  const endY = useRef(null);
  const isDragging = useRef(false);
  const tapTimeout = useRef(null);
  const { lockScroll, unlockScroll } = useScrollLock();
  // pointerY is the ABSOLUTE viewport y during a drag, not a delta — see
  // .claude/context/drawer-gestures.md.
  const [pointerY, setPointerY] = useState(0);
  // Visual viewport, not window.innerHeight — iOS doesn't shrink the layout viewport for the
  // keyboard. See .claude/context/drawer-gestures.md.
  const [viewportHeight, setViewportHeight] = useState(
    () => window.visualViewport?.height ?? window.innerHeight
  );
  const [viewportOffsetTop, setViewportOffsetTop] = useState(
    () => window.visualViewport?.offsetTop ?? 0
  );
  const [cachedHeight, setCachedHeight] = useState(null);
  // Read as React state (not localStorage) because the drag path re-renders ~60fps.
  // See .claude/context/drawer-gestures.md.
  const [liveAgent] = useLiveAgentSession();

  useEffect(() => {
    const visualViewport = window.visualViewport;
    if (!visualViewport) return undefined;

    // height tracks the keyboard, offsetTop tracks WebKit's reveal-on-scroll — both needed.
    const syncViewport = () => {
      setViewportHeight(visualViewport.height);
      setViewportOffsetTop(visualViewport.offsetTop);
    };
    visualViewport.addEventListener('resize', syncViewport);
    visualViewport.addEventListener('scroll', syncViewport);

    return () => {
      visualViewport.removeEventListener('resize', syncViewport);
      visualViewport.removeEventListener('scroll', syncViewport);
    };
  }, []);

  // Percentages must resolve against the visual viewport, not the layout one (`vh`), or the
  // drawer jumps the moment a drag starts. See .claude/context/drawer-gestures.md.
  const toViewportPx = (percentage) => `${(percentage / 100) * viewportHeight}px`;

  const throttledCalculateHeight = throttle(
    16, // So UI animation is ~60fps
    (currentPointerY) => {
      // Height is the distance from the finger to the bottom of the viewport, as a
      // percentage: finger near the top → tall drawer, finger near the bottom → short one.
      const dragRatio = Math.min(Math.abs(currentPointerY / viewportHeight), 1.0) * Math.sign(currentPointerY);
      const heightPercentage = (1 - dragRatio) * 100;
      const clampedHeight = Math.max(DRAWER_HEIGHTS.CLOSED, Math.min(heightPercentage, DRAWER_HEIGHTS.OPEN));
      // Adjust the height based on the drawer state max/min values
      const finalHeight = drawerState === 'MINIMIZED' ? Math.min(MINIMIZED_DRAG_MAX, clampedHeight)
        : Math.max(OPEN_DRAG_MAX, clampedHeight);
      const result = toViewportPx(finalHeight);
      setCachedHeight(result);
      return result;
    });

  const dynamicHeight = useMemo(() => {
    if (drawerState === 'OPEN' && !pointerY) {
      return `calc(${viewportHeight}px - 1em)`;
    }
    if (!pointerY) {
      return drawerState === 'MINIMIZED' ? currentHeight : toViewportPx(currentHeight);
    }
    const newHeight = throttledCalculateHeight(pointerY);
    return newHeight || cachedHeight || toViewportPx(currentHeight);
  }, [drawerState, pointerY, currentHeight, cachedHeight, viewportHeight]);

  // Anchors to the unobscured (visual) viewport bottom, not the layout viewport, so the
  // composer stays above the keyboard. See .claude/context/drawer-gestures.md.
  const dynamicBottom = useMemo(() => {
    if (drawerState !== 'OPEN') return undefined;
    return `calc(100% - ${viewportOffsetTop + viewportHeight}px)`;
  }, [drawerState, viewportOffsetTop, viewportHeight]);

  const handlePointerDown = useCallback((event) => {
    startY.current = event?.clientY || event.touches?.[0]?.clientY;
    endY.current = startY.current;

    tapTimeout.current = setTimeout(() => {
      if (startY.current !== null) {
        isDragging.current = true;
      }
    }, TAP_DURATION_MS);
  }, []);

  const handlePointerMove = useCallback((event) => {
    if (startY.current === null) return;

    endY.current = event?.clientY || event.touches?.[0]?.clientY;
    setPointerY(endY.current);
  }, []);

  const handleTap = () => {
    setDrawerState(drawerState === 'OPEN' ? 'MINIMIZED' : 'OPEN');
    setPointerY(null);
  };

  const handleDragEnd = useCallback(() => {
    if (startY.current !== null && endY.current !== null) {
      const deltaY = endY.current - startY.current;

      let newDrawerState = drawerState;
      if (drawerState === 'MINIMIZED') {
        if (deltaY >= (DRAG_THRESHOLD_PX / 2)) newDrawerState = 'CLOSED';
        if (deltaY <= -DRAG_THRESHOLD_PX) newDrawerState = 'OPEN';
      }
      if (drawerState === 'OPEN') {
        if (deltaY >= DRAG_THRESHOLD_PX) {

          newDrawerState = liveAgent.isEscalated ? 'CLOSED' : 'MINIMIZED';
        }
      }
      setDrawerState(newDrawerState);
      setPointerY(null);
    }
  }, [drawerState, liveAgent.isEscalated]);

  const handlePointerUp = useCallback(() => {
    clearTimeout(tapTimeout.current);

    if (isDragging.current) {
      handleDragEnd();
    } else {
      handleTap();
    }

    startY.current = null;
    endY.current = null;
    isDragging.current = false;
  }, [handleDragEnd, handleTap]);

  useEffect(() => {
    if (drawerState === 'OPEN') {
      lockScroll();
    } else {
      unlockScroll();
    }
  }, [drawerState, lockScroll, unlockScroll]);

  // Needed to ensure scroll is unlocked if component is closed/unmounted
  useEffect(() => {
    return () => {
      unlockScroll();
    };
  }, [unlockScroll]);

  return ({
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    dynamicHeight,
    dynamicBottom,
  });
};
